import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Database } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const CONF = {
  high: "bg-emerald-100 text-emerald-700", medium: "bg-sky/70 text-loop",
  low: "bg-amber-100 text-amber-700", needs_review: "bg-red-100 text-red-700",
};
const STEPS = ["Columns", "Stages", "Options", "Review"];

function TargetSelect({ catalog, value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-sky bg-white px-2 py-1.5 text-xs text-navy outline-none focus:border-loop">
      {catalog.groups.map((g) => (
        <optgroup key={g.group} label={g.label}>
          {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export default function CrmImportWizard({ open, onOpenChange, preview, onConfirmed }) {
  const [step, setStep] = useState(0);
  const [colMap, setColMap] = useState({});
  const [stageMap, setStageMap] = useState({});
  const [assignment, setAssignment] = useState("unassigned");
  const [assignUser, setAssignUser] = useState("");
  const [dup, setDup] = useState("skip");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applyTplId, setApplyTplId] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [ownerValues, setOwnerValues] = useState([]);
  const [ownerMap, setOwnerMap] = useState({});
  const [ownerLoading, setOwnerLoading] = useState(false);

  const catalog = preview?.target_catalog;
  const columns = preview?.columns || [];
  const stageValues = preview?.stage_values || [];
  const stages = catalog?.stages || [];
  const lostReasons = catalog?.lost_reasons || [];
  const subsByStage = useMemo(() => {
    const m = {};
    (catalog?.sub_stages || []).forEach((s) => { (m[s.stage_key] = m[s.stage_key] || []).push(s); });
    return m;
  }, [catalog]);
  const crmTemplates = preview?.crm_templates || [];

  // init from auto-mapping each time a new file is previewed
  useEffect(() => {
    if (!open || !preview) return;
    setStep(0); setResult(null);
    setColMap(Object.fromEntries(columns.map((c) => [c.header, c.target])));
    setStageMap(Object.fromEntries(stageValues.map((s) => [s.value, { stage: s.suggested_stage, reason: s.suggested_reason || "" }])));
    setAssignment("unassigned"); setAssignUser(""); setDup("skip");
    setApplyTplId(""); setSaveAsTemplate(false); setTemplateName("");
    setOwnerValues([]); setOwnerMap({}); setOwnerLoading(false);
  }, [open, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  // The CSV column currently mapped to the previous CRM owner / responsible person.
  const ownerColumn = useMemo(
    () => Object.keys(colMap).find((h) => colMap[h] === "previous_crm_owner") || "",
    [colMap]);

  const loadOwnerValues = async () => {
    if (!ownerColumn) { setOwnerValues([]); return; }
    setOwnerLoading(true);
    try {
      const { data } = await api.post("/imports/crm/owner-values", {
        preview_id: preview.preview_id, owner_column: ownerColumn,
      });
      const owners = data.data.owners || [];
      setOwnerValues(owners);
      setOwnerMap((prev) => {
        const next = { ...prev };
        owners.forEach((o) => { if (next[o.value] === undefined) next[o.value] = o.suggested_user_id || ""; });
        return next;
      });
    } catch (e) { toast.error(apiError(e)); }
    finally { setOwnerLoading(false); }
  };

  // Lazily fetch distinct CRM owners when the admin chooses the owner-mapping option.
  useEffect(() => {
    if (assignment === "owner_column" && ownerColumn) loadOwnerValues();
  }, [assignment, ownerColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = (tplId) => {
    setApplyTplId(tplId);
    const t = crmTemplates.find((x) => x.id === tplId);
    if (!t) return;
    setColMap((m) => {
      const next = { ...m };
      Object.entries(t.column_map || {}).forEach(([h, v]) => { if (h in next) next[h] = v; });
      return next;
    });
    setStageMap((m) => {
      const next = { ...m };
      Object.entries(t.crm_stage_mappings || {}).forEach(([s, v]) => { if (s in next) next[s] = v; });
      return next;
    });
    if (t.assignment_option && (t.assignment_option === "unassigned" || t.assignment_option === "owner_column" || t.assignment_option === "rules")) setAssignment(t.assignment_option);
    if (t.duplicate_option) setDup(t.duplicate_option);
    toast.success(`Applied template "${t.template_name}"`);
  };

  useEffect(() => {
    if (open && users.length === 0) api.get("/users").then(({ data }) => setUsers((data.data || []).filter((u) => u.status === "Active"))).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- preview/review computations ----
  const warnings = useMemo(() => {
    const w = [];
    const targets = Object.values(colMap);
    const crit = ["name", "phone", "email"];
    crit.forEach((c) => { if (!targets.includes(c)) w.push(`No column is mapped to ${c}.`); });
    crit.forEach((c) => { if (targets.filter((t) => t === c).length > 1) w.push(`Multiple columns map to ${c}.`); });
    return w;
  }, [colMap]);

  const stageBreakdown = useMemo(() => {
    const b = {};
    stageValues.forEach((s) => { const t = stageMap[s.value]?.stage || "New Lead"; b[t] = (b[t] || 0) + s.count; });
    return b;
  }, [stageMap, stageValues]);

  const totalRows = preview?.total_rows ?? preview?.row_count ?? columns.reduce((m, c) => Math.max(m, c.non_empty_count), 0);
  const ignoredCount = columns.filter((c) => colMap[c.header] === "__ignore__").length;
  const formAnswerCount = columns.filter((c) => colMap[c.header] === "form_answer").length;

  const setColTarget = (header, target) => setColMap((m) => ({ ...m, [header]: target }));
  const setStage = (val, patch) => setStageMap((m) => ({ ...m, [val]: { ...m[val], ...patch } }));

  const assignmentOption = assignment === "user" ? `user:${assignUser}` : assignment;

  const confirm = async () => {
    if (assignment === "user" && !assignUser) return toast.error("Select a user to assign leads to.");
    if (saveAsTemplate && !applyTplId && !templateName.trim()) return toast.error("Enter a name to save this mapping as a template.");
    setBusy(true);
    try {
      const { data } = await api.post("/imports/crm/confirm", {
        preview_id: preview.preview_id, column_mapping: colMap, stage_mapping: stageMap,
        assignment_option: assignmentOption, duplicate_option: dup,
        owner_map: assignment === "owner_column" ? ownerMap : null,
        save_as_template: saveAsTemplate,
        template_name: saveAsTemplate ? (templateName.trim() || (crmTemplates.find((t) => t.id === applyTplId)?.template_name)) : null,
        update_template_id: saveAsTemplate && applyTplId && !templateName.trim() ? applyTplId : null,
      });
      setResult(data.data);
      toast.success(`Imported ${data.data.new_leads} new lead(s)`);
      onConfirmed?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!preview) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[94vh] max-w-4xl overflow-y-auto border-sky bg-white" data-testid="crm-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy">
            <Database className="h-5 w-5 text-emerald-600" />
            CRM Migration Import
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="detected-type">
              Detected: {preview.detected_import_type === "bitrix" ? "Bitrix CRM Export" : preview.detected_import_type}
            </span>
          </DialogTitle>
          <DialogDescription>{preview.file_name} · {totalRows} rows · {columns.length} columns</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 py-4 text-center" data-testid="crm-import-result">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="font-sora text-lg font-bold text-navy">Import complete</p>
            <div className="mx-auto grid max-w-md grid-cols-4 gap-2">
              {[["New", result.new_leads], ["Updated", result.updated_leads], ["Dupes", result.duplicates_skipped], ["Issues", result.issues_count]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-mist px-3 py-2"><p className="font-sora text-lg font-bold text-navy">{v}</p><p className="text-[11px] text-ink/50">{l}</p></div>
              ))}
            </div>
            <p className="text-xs text-ink/50">Stage placement: {Object.entries(result.stage_breakdown || {}).map(([s, n]) => `${s} (${n})`).join(", ") || "—"}</p>
            <button data-testid="crm-wizard-close" onClick={() => onOpenChange(false)} className="rounded-xl bg-loop px-5 py-2 text-sm font-semibold text-white">Done</button>
          </div>
        ) : (
          <>
            {/* Stepper */}
            <div className="flex items-center gap-2">
              {STEPS.map((s, i) => (
                <div key={s} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${i === step ? "bg-loop text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-mist text-ink/50"}`}>
                  <span className="grid h-4 w-4 place-items-center rounded-full bg-white/30 text-[10px]">{i + 1}</span>{s}
                </div>
              ))}
            </div>

            {/* Saved CRM templates — apply a previously saved column + stage mapping */}
            {crmTemplates.length > 0 && step === 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky/60 bg-mist/40 px-3 py-2" data-testid="crm-template-apply">
                <span className="text-xs font-semibold text-ink/60">Apply saved template:</span>
                <select data-testid="crm-template-select" value={applyTplId} onChange={(e) => applyTemplate(e.target.value)} className="rounded-lg border border-sky bg-white px-2 py-1.5 text-xs text-navy outline-none focus:border-loop">
                  <option value="">— none —</option>
                  {crmTemplates.map((t) => <option key={t.id} value={t.id}>{t.template_name}</option>)}
                </select>
              </div>
            )}
            {step === 0 && (
              <div className="space-y-2" data-testid="step-columns">
                <p className="text-xs text-ink/55">Auto-mapped {columns.filter((c) => !["__ignore__", "form_answer"].includes(colMap[c.header])).length} columns · {ignoredCount} empty ignored · {formAnswerCount} stored as form answers. Override any mapping below.</p>
                <div className="overflow-x-auto rounded-xl border border-sky/60">
                  <table className="min-w-full text-xs">
                    <thead className="bg-mist/60 text-left text-ink/50"><tr><th className="px-3 py-2">CSV Column</th><th className="px-3 py-2">Samples</th><th className="px-3 py-2">Non-empty</th><th className="px-3 py-2">Confidence</th><th className="px-3 py-2 w-64">Maps to</th></tr></thead>
                    <tbody className="divide-y divide-sky/40">
                      {columns.map((c) => (
                        <tr key={c.header} data-testid={`col-row-${c.norm_key || c.header}`} className={colMap[c.header] === "__ignore__" ? "opacity-50" : ""}>
                          <td className="px-3 py-2 font-medium text-navy">{c.header || <em className="text-ink/40">(blank)</em>}</td>
                          <td className="px-3 py-2 text-ink/55">{(c.samples || []).join(", ").slice(0, 40) || "—"}</td>
                          <td className="px-3 py-2 text-ink/60">{c.non_empty_count}</td>
                          <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${CONF[c.confidence] || "bg-mist text-ink/60"}`}>{(c.confidence || "").replace("_", " ")}</span></td>
                          <td className="px-3 py-2"><TargetSelect catalog={catalog} value={colMap[c.header] || "__ignore__"} onChange={(v) => setColTarget(c.header, v)} testid={`col-target-${c.norm_key || c.header}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* STEP 1: Stages */}
            {step === 1 && (
              <div className="space-y-2" data-testid="step-stages">
                <p className="text-xs text-ink/55">Map each CRM stage to an AcquireLoop stage. The original is preserved as <code>previous_crm_stage</code>. Unknown stages default to New Lead.</p>
                {stageValues.length === 0 ? <p className="rounded-lg bg-mist p-3 text-sm text-ink/60">No stage column mapped — leads will use New Lead.</p> : (
                  <div className="overflow-x-auto rounded-xl border border-sky/60">
                    <table className="min-w-full text-xs">
                      <thead className="bg-mist/60 text-left text-ink/50"><tr><th className="px-3 py-2">Bitrix Stage</th><th className="px-3 py-2">Leads</th><th className="px-3 py-2 w-44">AcquireLoop Stage</th><th className="px-3 py-2 w-44">Sub-stage</th><th className="px-3 py-2 w-44">Lost/Junk reason</th></tr></thead>
                      <tbody className="divide-y divide-sky/40">
                        {stageValues.map((s) => {
                          const selStage = stageMap[s.value]?.stage || "New Lead";
                          const subs = subsByStage[selStage] || [];
                          return (
                          <tr key={s.value} data-testid={`stage-row-${s.value}`}>
                            <td className="px-3 py-2 font-medium text-navy">{s.value}{s.note && <span className="ml-1 text-[10px] text-amber-600">⚠ {s.note}</span>}</td>
                            <td className="px-3 py-2 text-ink/60">{s.count}</td>
                            <td className="px-3 py-2">
                              <select data-testid={`stage-target-${s.value}`}
                                value={selStage}
                                onChange={(e) => setStage(s.value, { stage: e.target.value, sub_stage: "" })}
                                className="w-full rounded-lg border border-sky bg-white px-2 py-1.5 text-xs text-navy outline-none focus:border-loop">
                                {[...new Set([...stages, stageMap[s.value]?.stage].filter(Boolean))].map((st) => (
                                  <option key={st} value={st}>{st}{!stages.includes(st) ? " (inactive)" : ""}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              {subs.length ? (
                                <select data-testid={`substage-target-${s.value}`} value={stageMap[s.value]?.sub_stage || ""} onChange={(e) => setStage(s.value, { sub_stage: e.target.value })} className="w-full rounded-lg border border-sky bg-white px-2 py-1.5 text-xs text-navy outline-none focus:border-loop">
                                  <option value="">— No sub-stage —</option>
                                  {subs.map((sub) => <option key={sub.key} value={sub.key}>{sub.name}</option>)}
                                </select>
                              ) : <span className="text-ink/30" title="This stage has no sub-stages">—</span>}
                            </td>
                            <td className="px-3 py-2">
                              {["Lost", "Junk"].includes(stageMap[s.value]?.stage) ? (
                                <select data-testid={`stage-reason-${s.value}`} value={stageMap[s.value]?.reason || ""} onChange={(e) => setStage(s.value, { reason: e.target.value })} className="w-full rounded-lg border border-sky bg-white px-2 py-1.5 text-xs text-navy outline-none focus:border-loop">
                                  <option value="">— none —</option>
                                  {[...new Set([...(stageMap[s.value]?.reason ? [stageMap[s.value].reason] : []), ...lostReasons])].map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                              ) : <span className="text-ink/30">—</span>}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Options */}
            {step === 2 && (
              <div className="space-y-5" data-testid="step-options">
                <div>
                  <p className="mb-2 font-sora text-sm font-semibold text-navy">Assignment</p>
                  {[["unassigned", "Keep imported leads unassigned (recommended for migrations)"], ["user", "Assign all imported leads to a selected user"], ["owner_column", "Map each CRM owner / responsible person to an AcquireLoop user"], ["rules", "Use existing AcquireLoop assignment rules"]].map(([v, label]) => (
                    <label key={v} data-testid={`assign-opt-${v}`} className="flex items-center gap-2 py-1 text-sm text-navy">
                      <input type="radio" name="assign" checked={assignment === v} onChange={() => setAssignment(v)} /> {label}
                    </label>
                  ))}
                  {assignment === "user" && (
                    <select data-testid="assign-user-select" value={assignUser} onChange={(e) => setAssignUser(e.target.value)} className="mt-1 w-72 rounded-lg border border-sky bg-white px-2 py-1.5 text-sm outline-none focus:border-loop">
                      <option value="">Select user…</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  )}
                  {assignment === "owner_column" && (
                    <div className="mt-2 rounded-xl border border-sky/60 bg-mist/40 p-3" data-testid="owner-mapping">
                      {!ownerColumn ? (
                        <p className="flex items-center gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />No column is mapped to <b>Previous CRM owner</b>. Go back to the Columns step and map the owner / responsible column first.</p>
                      ) : ownerLoading ? (
                        <p className="flex items-center gap-2 text-xs text-ink/60"><Loader2 className="h-3.5 w-3.5 animate-spin" />Reading owners from “{ownerColumn}”…</p>
                      ) : ownerValues.length === 0 ? (
                        <p className="text-xs text-ink/60">No owner values found in the “{ownerColumn}” column.</p>
                      ) : (
                        <>
                          <p className="mb-2 text-xs text-ink/55">Map each CRM owner to an AcquireLoop user. Unmapped owners stay unassigned. <b className="text-navy">{ownerValues.filter((o) => ownerMap[o.value]).length}/{ownerValues.length}</b> mapped.</p>
                          <div className="max-h-60 overflow-y-auto rounded-lg border border-sky/60">
                            <table className="min-w-full text-xs">
                              <thead className="sticky top-0 bg-mist/80 text-left text-ink/50"><tr><th className="px-3 py-2">CRM Owner</th><th className="px-3 py-2">Leads</th><th className="px-3 py-2 w-56">AcquireLoop User</th></tr></thead>
                              <tbody className="divide-y divide-sky/40">
                                {ownerValues.map((o) => (
                                  <tr key={o.value} data-testid={`owner-row-${o.value}`}>
                                    <td className="px-3 py-2 font-medium text-navy">{o.value}{!o.suggested_user_id && !ownerMap[o.value] && <span className="ml-1 text-[10px] text-amber-600">no match</span>}</td>
                                    <td className="px-3 py-2 text-ink/60">{o.count}</td>
                                    <td className="px-3 py-2">
                                      <select data-testid={`owner-select-${o.value}`} value={ownerMap[o.value] || ""} onChange={(e) => setOwnerMap((m) => ({ ...m, [o.value]: e.target.value }))} className="w-full rounded-lg border border-sky bg-white px-2 py-1.5 text-xs text-navy outline-none focus:border-loop">
                                        <option value="">— Keep unassigned —</option>
                                        {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                                      </select>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <p className="mb-2 font-sora text-sm font-semibold text-navy">Duplicate handling (matched by phone / email)</p>
                  {[["skip", "Skip duplicates (keep existing lead untouched)"], ["update", "Update existing — fill only empty fields, always append imported notes"], ["create", "Import as a new lead anyway"]].map(([v, label]) => (
                    <label key={v} data-testid={`dup-opt-${v}`} className="flex items-center gap-2 py-1 text-sm text-navy">
                      <input type="radio" name="dup" checked={dup === v} onChange={() => setDup(v)} /> {label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3: Review */}
            {step === 3 && (
              <div className="space-y-3" data-testid="step-review">
                {warnings.length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="review-warnings">
                    {warnings.map((w) => <p key={w} className="flex items-center gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />{w}</p>)}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[["Total rows", totalRows], ["Mapped cols", columns.length - ignoredCount - formAnswerCount], ["Form answers", formAnswerCount], ["Ignored empty", ignoredCount]].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-mist px-3 py-2 text-center"><p className="font-sora text-lg font-bold text-navy">{v}</p><p className="text-[11px] text-ink/50">{l}</p></div>
                  ))}
                </div>
                <div className="rounded-xl border border-sky/60 p-3 text-sm">
                  <p className="text-ink/60">Assignment: <b className="text-navy">{assignment === "user" ? `Assign to ${users.find((u) => u.id === assignUser)?.name || "—"}` : assignment === "unassigned" ? "Keep unassigned" : assignment === "owner_column" ? `Owner mapping (${ownerValues.filter((o) => ownerMap[o.value]).length}/${ownerValues.length} owners mapped)` : "AcquireLoop rules"}</b> · Duplicates: <b className="text-navy">{dup}</b></p>
                  <p className="mt-1 text-ink/60">Stage placement: {Object.entries(stageBreakdown).map(([s, n]) => `${s} (${n})`).join(", ") || "All → New Lead"}</p>
                </div>
                <div className="rounded-xl border border-sky/60 p-3" data-testid="crm-save-template">
                  <label className="flex items-center gap-2 text-sm font-medium text-navy">
                    <input type="checkbox" data-testid="crm-save-template-toggle" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} className="accent-loop" />
                    Save this column &amp; stage mapping as a reusable template
                  </label>
                  {saveAsTemplate && (
                    <div className="mt-2">
                      {applyTplId ? (
                        <p className="text-xs text-ink/55">Will update "<b className="text-navy">{crmTemplates.find((t) => t.id === applyTplId)?.template_name}</b>". To save a new one instead, type a different name below.</p>
                      ) : null}
                      <input data-testid="crm-template-name-input" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder={applyTplId ? "New template name (optional)" : "Template name (e.g. Bitrix Deals export)"} className="mt-1 w-full rounded-lg border border-sky bg-white px-2.5 py-1.5 text-sm text-navy outline-none focus:border-loop" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-sky/50 pt-3">
              <button data-testid="wizard-back" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 text-sm font-semibold text-navy disabled:opacity-40"><ArrowLeft className="h-4 w-4" />Back</button>
              {step < STEPS.length - 1 ? (
                <button data-testid="wizard-next" onClick={() => setStep((s) => s + 1)} className="flex items-center gap-1 rounded-lg bg-loop px-4 py-1.5 text-sm font-semibold text-white">Next<ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button data-testid="wizard-confirm-import" disabled={busy} onClick={confirm} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Import {totalRows} leads</button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
